import React, { useState, useEffect } from 'react';
import { motivoLegivel } from '@/services/erroLegivel';
import {
  strategistService,
  type AnticipationPlannerResponse,
  type PrescriptionItem,
  type ProductDemand
} from '@/services/strategistService';
import { AnticipationTimeline } from '@/components/Strategist/AnticipationTimeline';
import { StrategistChatDrawer } from '@/components/Strategist/StrategistChatDrawer';

export const InteractivePlaybook: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [planner, setPlanner] = useState<AnticipationPlannerResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const fetchPlanner = async (m: number) => {
    setLoading(true);
    try {
      const data = await strategistService.getPlanner(m);
      setPlanner(data);
    } catch (err) {
      // Sem estado de erro visivel, a falha deixava o dado nulo e a tela
      // ficava em "carregando" para sempre — que foi como as telas do
      // Estrategista se apresentaram durante toda a auditoria.
      setErro(motivoLegivel(err));
      console.error('Erro ao carregar o planner de antecipação:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanner(selectedMonth);
  }, [selectedMonth]);

  const handlePrescribeAction = async (action: { title: string; category: string; lead_time: number; affiliates?: string[] }) => {
    try {
      const res = await strategistService.prescribe({
        title: action.title,
        category: action.category,
        lead_time_days: action.lead_time,
        affiliates: action.affiliates
      });
      setToastMsg(res.message);
      setTimeout(() => setToastMsg(null), 4500);
    } catch {
      setToastMsg('Falha ao prescrever campanha no Kanban Estratégico.');
      setTimeout(() => setToastMsg(null), 4000);
    }
  };

  const handlePrescribeFromCard = (p: PrescriptionItem) => {
    handlePrescribeAction({
      title: p.titulo,
      category: p.categoria,
      lead_time: 45
    });
  };

  const handlePrescribeFromProduct = (prod: ProductDemand) => {
    handlePrescribeAction({
      title: `Guia de Compras Estratégico: ${prod.produto_nome}`,
      category: prod.categoria_mercado,
      lead_time: 45
    });
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1600px', margin: '0 auto', color: 'var(--text-1)' }}>
      {/* Toast Notification */}
      {toastMsg && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          backgroundColor: 'var(--bg-1)',
          border: '1px solid var(--sig-ok-border)',
          color: 'var(--sig-ok-text)',
          padding: '12px 20px',
          borderRadius: 'var(--r-md)',
          fontWeight: 600,
          fontSize: 'var(--fs-sm)',
          boxShadow: 'var(--shadow-3)',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: '14px'
        }}>
          <span>{toastMsg}</span>
          <a
            href="/inteligencia/kanban-estrategico"
            style={{
              padding: '6px 12px',
              backgroundColor: 'var(--nexus-brand)',
              color: 'var(--text-on-accent)',
              borderRadius: 'var(--r-sm)',
              textDecoration: 'none',
              fontSize: 'var(--fs-xs)',
              fontWeight: 700
            }}
          >
            Ver no Kanban Estratégico →
          </a>
        </div>
      )}

      {/* Header Executivo do Playbook */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px', borderBottom: '1px solid var(--border-1)', paddingBottom: '18px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
              Playbook Interativo & Planner de Antecipação
            </h1>
            <span style={{
              backgroundColor: 'var(--nexus-brand-bg)',
              color: 'var(--nexus-brand)',
              border: '1px solid var(--border-2)',
              padding: '2px 8px',
              borderRadius: 'var(--r-sm)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase'
            }}>
              Estrategista Prescritivo
            </span>
          </div>
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-3)', margin: '4px 0 0 0', lineHeight: 1.5 }}>
            Calendário comercial e editorial do ano, montado sobre fontes públicas
            nacionais (IBGE, ANAC, Receita Federal e Google Trends). Base de
            referência compartilhada, não um estudo do seu mercado.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <a
            href="/inteligencia/kanban-estrategico"
            style={{
              padding: '8px 14px',
              backgroundColor: 'var(--bg-1)',
              color: 'var(--text-1)',
              border: '1px solid var(--border-2)',
              borderRadius: 'var(--r-md)',
              textDecoration: 'none',
              fontSize: 'var(--fs-sm)',
              fontWeight: 600
            }}
          >
            Kanban Estratégico
          </a>

          <button
            onClick={() => setIsChatOpen(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--nexus-brand)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 'var(--r-md)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Copiloto Estrategista
          </button>

          <a
            href="/config/estrategista"
            style={{
              padding: '8px 14px',
              backgroundColor: 'var(--bg-1)',
              color: 'var(--text-2)',
              border: '1px solid var(--border-2)',
              borderRadius: 'var(--r-md)',
              textDecoration: 'none',
              fontSize: 'var(--fs-sm)',
              fontWeight: 600
            }}
          >
            Configurações
          </a>
        </div>
      </div>

      {/* Timeline de Navegação das 52 Semanas */}
      <AnticipationTimeline
        currentMonth={selectedMonth}
        onSelectMonth={(m) => setSelectedMonth(m)}
      />

      {erro ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-3)' }}>
          <p style={{ color: 'var(--text-1)', fontWeight: 600, marginBottom: '6px' }}>
            Não foi possível carregar o planner.
          </p>
          <p style={{ fontSize: '13px' }}>{erro}</p>
        </div>
      ) : loading || !planner ? (
        <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-3)' }}>
          Calculando derivadas, volumes de liquidez e carregando o planner…
        </div>
      ) : (
        <>
          {/* Grid de Métricas Principais do Mês */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            {/* Card 1: Liquidez */}
            <div style={{ backgroundColor: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--r-md)', padding: '16px' }}>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sig-ok-text)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Liquidez Injetada no Mês
              </div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-1)', margin: '4px 0 2px 0' }}>
                R$ {planner.liquidez_total_mes_bi_rs > 0 ? `${planner.liquidez_total_mes_bi_rs.toFixed(1)} Bilhões` : 'Fluxo Regular'}
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)' }}>
                {planner.eventos_liquidez.length > 0
                  ? planner.eventos_liquidez.map((e) => e.evento_liquidez).join(' + ')
                  : 'Salários ordinários e consumo difuso de varejo.'}
              </div>
            </div>

            {/* Card 2: Segmento Dominante */}
            <div style={{ backgroundColor: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--r-md)', padding: '16px' }}>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--nexus-brand)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Segmento Líder de Vendas
              </div>
              <div style={{ fontSize: '17px', fontWeight: 800, color: 'var(--text-1)', margin: '4px 0 2px 0' }}>
                {planner.ecommerce_dados.segmento_dominante || 'Varejo Multissetorial'}
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)' }}>
                Ticket Médio: <strong>R$ {planner.ecommerce_dados.ticket_medio_rs?.toFixed(2) || '450.00'}</strong>
              </div>
            </div>

            {/* Card 3: Início de Pesquisas de Preço */}
            <div style={{ backgroundColor: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--r-md)', padding: '16px' }}>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--warn)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Início das Pesquisas por Preço
              </div>
              <div style={{ fontSize: '17px', fontWeight: 800, color: 'var(--text-1)', margin: '4px 0 2px 0' }}>
                {planner.ecommerce_dados.inicio_pesquisa_preco || 'D-45 do Evento'}
              </div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)' }}>
                Pico de Compras: <strong>{planner.ecommerce_dados.pico_compras_transacoes || 'Na semana do evento'}</strong>
              </div>
            </div>
          </div>

          {/* Seção 1: Prescrições Estratégicas em Ação */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{ borderLeft: '3px solid var(--nexus-brand)', paddingLeft: '10px', marginBottom: '14px' }}>
              <h2 style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-1)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Prescrições Executivas Obrigatórias ({planner.mes_nome})
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {planner.prescricoes_estrategicas.map((p) => (
                <div key={p.id} style={{ backgroundColor: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--r-md)', padding: '18px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--nexus-brand)', fontWeight: 700, textTransform: 'uppercase' }}>
                        {p.categoria}
                      </span>
                      <span style={{
                        fontSize: 'var(--fs-xxs)',
                        padding: '2px 6px',
                        borderRadius: 'var(--r-sm)',
                        backgroundColor: p.prioridade === 'CRÍTICA' ? 'var(--sig-crit-bg)' : 'var(--sig-info-bg)',
                        color: p.prioridade === 'CRÍTICA' ? 'var(--sig-crit-text)' : 'var(--sig-info-text)',
                        border: `1px solid ${p.prioridade === 'CRÍTICA' ? 'var(--sig-crit-border)' : 'var(--sig-info-border)'}`,
                        fontWeight: 700
                      }}>
                        {p.prioridade}
                      </span>
                    </div>

                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-1)', margin: '0 0 8px 0', lineHeight: 1.4 }}>
                      {p.titulo}
                    </h3>
                    <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-2)', lineHeight: 1.5, margin: '0 0 12px 0' }}>
                      {p.descricao}
                    </p>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sig-ok-text)', fontWeight: 600, marginBottom: '14px' }}>
                      Impacto Estimado: {p.impacto_estimado_rs}
                    </div>
                  </div>

                  <button
                    onClick={() => handlePrescribeFromCard(p)}
                    style={{
                      width: '100%',
                      padding: '9px',
                      backgroundColor: 'var(--nexus-brand)',
                      color: 'var(--text-on-accent)',
                      border: 'none',
                      borderRadius: 'var(--r-md)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Lançar no Kanban Estratégico
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Seção 2: Produtos em Alta & Picos de Demanda */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{ borderLeft: '3px solid var(--chart-serie-5)', paddingLeft: '10px', marginBottom: '14px' }}>
              <h2 style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-1)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Produtos Líderes de Demanda & Lead Time Ideal ({planner.mes_nome})
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '16px' }}>
              {planner.produtos_destaque.map((prod, idx) => (
                <div key={idx} style={{ backgroundColor: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--r-md)', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <span style={{ fontSize: 'var(--fs-xxs)', color: 'var(--text-3)', textTransform: 'uppercase', fontWeight: 700 }}>
                      {prod.categoria_mercado}
                    </span>
                    <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-1)', margin: '4px 0 8px 0' }}>
                      {prod.produto_nome}
                    </h3>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-2)', marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                      <div>Preço Médio: <strong>R$ {prod.preco_medio_unitario_rs?.toFixed(2)}</strong></div>
                      <div>Pico de Vendas: <strong>{prod.epoca_pico_vendas}</strong></div>
                      <div style={{ color: 'var(--warn)', fontWeight: 600 }}>Lead Time: {prod.lead_time_antecipacao_ideal}</div>
                    </div>
                  </div>

                  <button
                    onClick={() => handlePrescribeFromProduct(prod)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      backgroundColor: 'var(--bg-2)',
                      border: '1px solid var(--border-2)',
                      color: 'var(--text-1)',
                      borderRadius: 'var(--r-md)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Lançar Campanha de Produto
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Drawer do Copiloto Estrategista */}
      <StrategistChatDrawer
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onPrescribeAction={handlePrescribeAction}
      />
    </div>
  );
};
